import React, { useState, useRef, useEffect } from 'react';
import './ChecklistPanel.css';
import { CloseIconSmall, CheckIcon, NotApplicableIcon, PendingIcon } from './ChecklistIcons';
import checklistData from './checklistData';
import axiosInstance from '../utils/axiosConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const ChecklistItem = ({ id, title, status, aiRecognition, onStatusChange }) => {
  return (
    <div className="checklist-item">
      <div className="checklist-item-title">
        {title}
      </div>
      <div className="checklist-item-status">
        <button 
          className={`checklist-status-button completed ${status === 'completed' ? 'active' : ''}`} 
          onClick={() => onStatusChange(id, 'completed')}
          title="Completed"
        >
          <CheckIcon />
        </button>
        <button 
          className={`checklist-status-button not-applicable ${status === 'not_applicable' ? 'active' : ''}`} 
          onClick={() => onStatusChange(id, 'not_applicable')}
          title="Not Applicable"
        >
          <NotApplicableIcon />
        </button>
        <button 
          className={`checklist-status-button pending ${status === 'pending' ? 'active' : ''}`} 
          onClick={() => onStatusChange(id, 'pending')}
          title="Pending"
        >
          <PendingIcon />
        </button>
      </div>
    </div>
  );
};

const ChecklistSection = ({ title, items, onStatusChange, isInitiallyOpen = false }) => {
  const [isOpen, setIsOpen] = useState(isInitiallyOpen);
  
  // Calculate section progress
  const getSectionProgress = () => {
    const completed = items.filter(item => item.status === 'completed').length;
    const notApplicable = items.filter(item => item.status === 'not_applicable').length;
    const total = items.length;
    const percentage = total > 0 ? Math.round(((completed + notApplicable) / total) * 100) : 0;
    
    return { completed, notApplicable, total, percentage };
  };
  
  const { completed, notApplicable, total, percentage } = getSectionProgress();
  
  return (
    <div className="checklist-section">
      <div className="checklist-section-header" onClick={() => setIsOpen(!isOpen)}>
        <div className="checklist-section-title-wrapper">
          {title}
          <div className="checklist-section-progress">
            <span className="checklist-section-progress-text">{completed + notApplicable}/{total}</span>
            <span className="checklist-section-progress-percentage">{percentage}%</span>
          </div>
        </div>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          style={{ 
            fontSize: '1rem', 
            color: '#4a6b25',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        />
      </div>
      
      {/* Section progress bar */}
      <div className="checklist-section-progress-bar">
        <div 
          className="checklist-section-progress-fill" 
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
      
      <div className={`checklist-section-content ${isOpen ? 'open' : ''}`}>
        {items.map(item => (
          <ChecklistItem 
            key={item.id} 
            id={item.id} 
            title={item.title} 
            status={item.status} 
            aiRecognition={item.aiRecognition} 
            onStatusChange={onStatusChange} 
          />
        ))}
      </div>
    </div>
  );
};

const ChecklistPanel = ({ isOpen, onClose, deliveryId, showSuccess, showError, onProgressUpdate, initialChecklistData, onChecklistChange }) => {
  const [checklistItems, setChecklistItems] = useState({
    preShipment: checklistData.preShipment,
    afterLoading: checklistData.afterLoading,
    afterDischarge: checklistData.afterDischarge
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState(null);
  
  const panelRef = useRef(null);
  
  // Reset hasUnsavedChanges when panel is closed and then reopened
  useEffect(() => {
    if (isOpen) {
      // Panel is opening
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);
  
  // Always update checklist items whenever initialChecklistData changes
  useEffect(() => {
    if (initialChecklistData) {
      console.log('Updating checklist items with initialChecklistData');
      setChecklistItems(initialChecklistData);
    }
  }, [initialChecklistData]);
  
  // Only fetch from backend when needed
  useEffect(() => {
    // Skip if we already have initial data or there's no valid delivery ID or panel isn't open
    if (initialChecklistData || !isOpen || !deliveryId || deliveryId === 'new' || deliveryId === 'undefined') {
      return;
    }
    
    const fetchChecklistData = async () => {
      try {
        setIsLoading(true);
        const response = await axiosInstance.get(`/api/deliveries/${deliveryId}/checklist/`);
        
        // Transform backend data structure to frontend structure
        const loadedData = {
          preShipment: response.data.pre_shipment_data || [],
          afterLoading: response.data.after_loading_data || [],
          afterDischarge: response.data.after_discharge_data || []
        };
        
        setChecklistItems(loadedData);
        
        // Notify parent component of the loaded data
        if (onChecklistChange) {
          onChecklistChange(loadedData);
        }
        
        console.log('Loaded checklist data from backend for delivery:', deliveryId);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching checklist data:', error);
        setError('Failed to load checklist data');
        setIsLoading(false);
        
        // Use default checklist data as fallback
        const defaultData = {
          preShipment: checklistData.preShipment,
          afterLoading: checklistData.afterLoading,
          afterDischarge: checklistData.afterDischarge
        };
        
        setChecklistItems(defaultData);
        
        // Notify parent component of the fallback data
        if (onChecklistChange) {
          onChecklistChange(defaultData);
        }
        
        // Show error message if showError function is available
        if (showError) {
          showError('Failed to load checklist data. Using default checklist.');
        }
      }
    };
    
    fetchChecklistData();
  }, [deliveryId, isOpen, showError, initialChecklistData, onChecklistChange]);

  // Calculate progress statistics
  const getProgressStats = () => {
    // Flatten all items from all three sections for calculation
    const allItems = [
      ...checklistItems.preShipment,
      ...checklistItems.afterLoading,
      ...checklistItems.afterDischarge
    ];
    
    const completed = allItems.filter(item => item.status === 'completed').length;
    const notApplicable = allItems.filter(item => item.status === 'not_applicable').length;
    const total = allItems.length;
    const percentage = Math.round(((completed + notApplicable) / total) * 100);
    
    return { completed, notApplicable, total, percentage };
  };
  
  const { completed, notApplicable, total, percentage } = getProgressStats();
  
  // Update parent component with progress
  useEffect(() => {
    if (onProgressUpdate) {
      const { completed, notApplicable, total } = getProgressStats();
      onProgressUpdate(completed + notApplicable, total);
    }
  }, [checklistItems]);
  
  // Handle clicks outside the panel to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target) && isOpen) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key to close panel
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Handle status change for an item
  const handleStatusChange = async (itemId, newStatus) => {
    // Update preShipment items
    const updatedPreShipment = checklistItems.preShipment.map(item => 
      item.id === itemId ? { ...item, status: newStatus } : item
    );
    
    // Update afterLoading items
    const updatedAfterLoading = checklistItems.afterLoading.map(item => 
      item.id === itemId ? { ...item, status: newStatus } : item
    );
    
    // Update afterDischarge items
    const updatedAfterDischarge = checklistItems.afterDischarge.map(item => 
      item.id === itemId ? { ...item, status: newStatus } : item
    );
    
    // Create updated items object for state update
    const updatedItems = {
      preShipment: updatedPreShipment,
      afterLoading: updatedAfterLoading,
      afterDischarge: updatedAfterDischarge
    };
    
    // Update local state immediately for responsiveness
    setChecklistItems(updatedItems);
    setHasUnsavedChanges(true);
    
    // Notify parent component of changes immediately
    if (onChecklistChange) {
      onChecklistChange(updatedItems);
    }
  };
  
  // Expose the current checklist data to parent components
  useEffect(() => {
    if (onChecklistChange && hasUnsavedChanges) {
      onChecklistChange(checklistItems);
    }
  }, [checklistItems, hasUnsavedChanges, onChecklistChange]);
  
  // If panel is closed, don't render anything
  if (!isOpen) return null;
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="checklist-panel-overlay">
        <div 
          ref={panelRef} 
          className="checklist-panel"
          role="dialog"
        >
          <div className="checklist-panel-header">
            <h2>Loading Delivery Checklist...</h2>
            <button 
              className="close-button" 
              onClick={onClose}
              aria-label="Close checklist panel"
            >
              <CloseIconSmall />
            </button>
          </div>
          <div className="checklist-content">
            <div className="checklist-loading">Loading...</div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="checklist-panel-overlay">
      <div 
        ref={panelRef} 
        className="checklist-panel"
        role="dialog"
        aria-labelledby="checklist-panel-title"
      >
        <div className="checklist-panel-header">
          <h2 id="checklist-panel-title">Delivery Checklist</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            aria-label="Close checklist panel"
          >
            <CloseIconSmall />
          </button>
        </div>
        
        <div className="checklist-content">
          <div className="checklist-progress">
            <div className="checklist-progress-title">
              <span>Overall Progress: {completed + notApplicable}/{total} completed</span>
              <span>{percentage}%</span>
            </div>
            <div className="checklist-progress-bar">
              <div 
                className="checklist-progress-fill" 
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
          </div>

          <ChecklistSection 
            title="Pre-shipment" 
            items={checklistItems.preShipment} 
            onStatusChange={handleStatusChange}
            isInitiallyOpen={true}
          />
          
          <ChecklistSection 
            title="After Loading" 
            items={checklistItems.afterLoading} 
            onStatusChange={handleStatusChange}
            isInitiallyOpen={true}
          />
          
          <ChecklistSection 
            title="After Discharge" 
            items={checklistItems.afterDischarge} 
            onStatusChange={handleStatusChange}
            isInitiallyOpen={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ChecklistPanel;
